import { HttpClient, HttpContext } from '@angular/common/http';
import { DestroyRef, inject, Injectable, type Signal,signal } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { parseStatusPageComponents, summarizeComponents } from '../../shared/utils/status-page.util';
import { SKIP_ACTIVITY_LOG } from '../interceptors/skip-activity-log.token';
import type { StatusPageSummary } from '../interfaces/status-page.interface';

/** Public, read-only components API of the Instaleap status page (the same one LSS reads). */
export const STATUS_PAGE_API_URL = 'https://pwjx9j7lm4tp.statuspage.io/api/v2/components.json';

/** Public status page that the quick menu links to. */
export const STATUS_PAGE_URL = 'https://status.instaleap.io/';

/** How often the status is read again while somebody is watching it. */
export const STATUS_REFRESH_MS = 5 * 60 * 1000;

/** Loading state of the status summary. */
export type StatusPageState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Reads the Instaleap status page and exposes a summary. A failure never throws: the state becomes
 * `error` and consumers fall back to just the link. The reading repeats every five minutes while
 * `watch()` is active.
 */
@Injectable({ providedIn: 'root' })
export class StatusPageService {
  private readonly http = inject(HttpClient);
  private readonly stateValue = signal<StatusPageState>('idle');
  private readonly summaryValue = signal<StatusPageSummary | null>(null);
  private timer: ReturnType<typeof setInterval> | null = null;
  private watchers = 0;

  /** Whether the last reading is loading, ready or failed. */
  readonly state: Signal<StatusPageState> = this.stateValue.asReadonly();
  /** Summary of the last successful reading, or `null` before the first one. */
  readonly summary: Signal<StatusPageSummary | null> = this.summaryValue.asReadonly();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stopTimer());
  }

  /** Reads the status page now. */
  async refresh(): Promise<void> {
    this.stateValue.set('loading');
    try {
      const payload = await firstValueFrom(
        this.http.get<unknown>(STATUS_PAGE_API_URL, { context: new HttpContext().set(SKIP_ACTIVITY_LOG, true) }),
      );
      this.summaryValue.set(summarizeComponents(parseStatusPageComponents(payload)));
      this.stateValue.set('ready');
    } catch {
      this.stateValue.set('error');
    }
  }

  /**
   * Starts reading the status now and every five minutes until the returned function is called.
   * Several watchers share one timer.
   */
  watch(): () => void {
    this.watchers++;
    if (this.watchers === 1) {
      void this.refresh();
      this.timer = setInterval(() => void this.refresh(), STATUS_REFRESH_MS);
    }
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      this.watchers--;
      if (this.watchers === 0) this.stopTimer();
    };
  }

  private stopTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}
